import React, { useMemo, useState } from "react";
import { usePersistedState } from "../../hooks/utility/usePersistedState";
import { useNavigate } from "react-router-dom";
import { Bar, Doughnut, Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  LineController,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { legendIsolateClickPie, legendIsolateClickDataset } from "../../Utils/chartLegend";
import { useGetPerSiteAnalytics } from "../../hooks/analytics/useGetPerSiteAnalytics";
import { useGetPerGroupAnalytics } from "../../hooks/analytics/useGetPerGroupAnalytics";
import { useGetEnergieKpis, ENERGIE_KPI_CODES, getKpiByCode } from "../../hooks/analytics/useGetKpisAnalytics";
import { useGetSites } from "../../hooks/site/useGetSites";
import { useGetBUs } from "../../hooks/reference/useGetBUs";
import { useGetActivites } from "../../hooks/reference/useGetActivites";
import { useGetFilieres } from "../../hooks/reference/useGetFilieres";
import { useGetRegions } from "../../hooks/reference/useGetRegions";
import { useGetPostesEmission } from "../../hooks/emission/useGetPostesEmission";
import { useGetPosteChampsMetier } from "../../hooks/metadata/useGetPosteChampsMetier";
import { useGetPosteDimensions } from "../../hooks/metadata/useGetPosteDimensions";
import { useGetRawEnergiePerTypeBU, type RawEnergieGroupEntry } from "../../hooks/analytics/useGetRawEnergiePerTypeBU";
import { useGetModuleAnalytics } from "../../hooks/analytics/useGetModuleAnalytics";
import { usePageTitle } from "../../hooks/utility/usePageTitle";
import Topbar from "Components/Topbar/Topbar";
import { MultiSearchableCombobox } from "Components/MultiSearchableCombobox";
import {
  Zap,
  Calendar,
  Filter,
  BarChart3,
  Activity,
  PieChart,
  MapPin,
  Layers,
  Fuel,
  Flame,
  Droplets,
  Package,
  ChevronDown,
} from "lucide-react";
import type { User as BackendUser } from "../../types/user";
import "./EnergyPage.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  LineController,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

interface EnergyPageProps {
  user: BackendUser;
}

const TEP_TO_GWH = 11630 / 1_000_000;
const KWH_TO_GWH = 1 / 1_000_000;

const ACCENT = "#f59e0b";

// Format large numbers like in Def_Energie: 40.5M, 5.2k, etc.
function formatLargeNumber(num: number): string {
  const absNum = Math.abs(num);
  let formatted: string;
  if (absNum >= 1_000_000) {
    formatted = `${(absNum / 1_000_000).toFixed(1)}M`;
  } else if (absNum >= 1_000) {
    formatted = `${(absNum / 1_000).toFixed(1)}k`;
  } else {
    formatted = absNum.toFixed(1);
  }
  return num < 0 ? `-${formatted}` : formatted;
}

const CHART_COLORS = [
  "#2563eb", // blue
  "#dc2626", // red
  "#16a34a", // green
  "#f59e0b", // amber
  "#9333ea", // purple
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#a16207", // brown
  "#64748b", // slate
  "#6366f1", // indigo
  "#e11d48", // rose
  "#0d9488", // teal
];

const BU_COLORS: Record<string, string> = {
  "PDTS FRAIS": "#16a34a",
  "PDTS TRSF": "#2563eb",
  DOMARKET: "#C22D86",
  AUTRES: "#f59e0b",
};

const ACTIVITE_COLORS: Record<string, string> = {
  VEGETALE: "#16a34a",
  "ELEVAGE + GC": "#2563eb",
  TRANSFORMATION: "#9333ea",
  COMMERCIALE: "#ec4899",
  AUTRES: "#f59e0b",
};

const FILIERE_COLORS: Record<string, string> = {
  AGRUMES: "#f59e0b",
  AGRUME: "#f59e0b",
  "FRUITS ROUGES": "#dc2626",
  LAIT: "#ec4899",
  MARAICHAGE: "#16a34a",
  CEREALES: "#a16207",
  OLIVIER: "#2563eb",
  AVOCAT: "#0d9488",
  "PLANTES AROMATIQUES": "#9333ea",
  "AUTRES ARBO": "#64748b",
  AUTRES: "#06b6d4",
};

function getGroupColor(
  name: string,
  colorMap: Record<string, string>,
  fallbackIndex: number
): string {
  const upper = name.toUpperCase().trim();
  if (colorMap[upper]) return colorMap[upper];
  for (const [key, color] of Object.entries(colorMap)) {
    if (upper.includes(key) || key.includes(upper)) return color;
  }
  return CHART_COLORS[fallbackIndex % CHART_COLORS.length];
}

// Energy type colors for the "Evolution par Type" chart
const ENERGY_TYPE_COLORS = {
  ELEC: "#3b82f6",
  GASOIL: "#16a34a",
  ESSENCE: "#8b5cf6",
  FUEL: "#ef4444",
  AUTRES: "#f59e0b",
};

// Helper function to group monthly time series data by campaigns
function groupByCampaign<T extends Record<string, any>>(timeSeries: T[]) {
  const campaignGroups = new Map<string, T[]>();
  timeSeries.forEach((ts) => {
    const month = ts.month;
    const [year, monthNum] = month.split('-').map(Number);
    const campaign = monthNum >= 10
      ? `${year % 100}/${(year + 1) % 100}`
      : `${(year - 1) % 100}/${year % 100}`;
    if (!campaignGroups.has(campaign)) {
      campaignGroups.set(campaign, []);
    }
    campaignGroups.get(campaign)!.push(ts);
  });
  return campaignGroups;
}

function buildPieData(
  data: { groupName: string; total: number }[],
  colorMap: Record<string, string>
) {
  if (data.length === 0) return null;
  return {
    labels: data.map((d) => d.groupName),
    datasets: [{
      data: data.map((d) => d.total),
      backgroundColor: data.map((d, i) => getGroupColor(d.groupName, colorMap, i)),
      borderWidth: 2,
      borderColor: "#fff",
    }],
  };
}

function buildStackedBarData(
  data: { groupName: string; timeSeries: { month: string; valeur: number }[] }[],
  months: string[],
  colorMap: Record<string, string>,
  isCampaignView: boolean = false
) {
  if (data.length === 0) return null;
  if (isCampaignView) {
    const allCampaigns = new Set<string>();
    const campaignDataByGroup = new Map<string, Map<string, number>>();
    data.forEach(item => {
      const campaignGroups = groupByCampaign(item.timeSeries);
      const campaignTotals = new Map<string, number>();
      campaignGroups.forEach((monthsInCampaign, campaign) => {
        allCampaigns.add(campaign);
        campaignTotals.set(campaign, monthsInCampaign.reduce((sum, t) => sum + t.valeur, 0));
      });
      campaignDataByGroup.set(item.groupName, campaignTotals);
    });
    const sortedCampaigns = Array.from(allCampaigns).sort();
    return {
      labels: sortedCampaigns,
      datasets: data.map((item, i) => {
        const campaignTotals = campaignDataByGroup.get(item.groupName)!;
        return {
          label: item.groupName,
          data: sortedCampaigns.map((c) => campaignTotals.get(c) || 0),
          backgroundColor: getGroupColor(item.groupName, colorMap, i) + "CC",
          borderRadius: i === data.length - 1 ? 6 : 0,
          borderSkipped: false as const,
        };
      }),
    };
  } else {
    if (months.length === 0) return null;
    return {
      labels: months,
      datasets: data.map((item, i) => {
        const monthMap = new Map(item.timeSeries.map((t) => [t.month, t.valeur]));
        return {
          label: item.groupName,
          data: months.map((m) => monthMap.get(m) || 0),
          backgroundColor: getGroupColor(item.groupName, colorMap, i) + "CC",
          borderRadius: i === data.length - 1 ? 6 : 0,
          borderSkipped: false as const,
        };
      }),
    };
  }
}

// ============================================================
// RAW ENERGY TYPE ANALYSIS — helpers
// ============================================================

// Known "individual" type codes (everything else is AUTRES)
const KNOWN_INDIVIDUAL_CODES = ["ELEC", "ELECTRICITE", "GASOIL", "ESSENCE", "ESS", "FUEL"];


// Build per-ha stacked bar data: energy / ha per BU per period
function buildPerHaBarData(
  energyData: { groupName: string; timeSeries: { month: string; valeur: number }[] }[],
  haData: { groupName: string; timeSeries: { month: string; valeur: number }[]; total: number }[],
  months: string[],
  colorMap: Record<string, string>,
  isCampaignView: boolean
) {
  if (energyData.length === 0 || haData.length === 0) return null;

  // Build ha lookup per BU: groupName → monthMap + total
  const haLookup = new Map<string, { monthMap: Map<string, number>; total: number }>();
  haData.forEach((ha) => {
    haLookup.set(ha.groupName, {
      monthMap: new Map(ha.timeSeries.map((t) => [t.month, t.valeur])),
      total: ha.total,
    });
  });

  if (isCampaignView) {
    const allCampaigns = new Set<string>();

    // Campaign totals for energy
    const energyCampByGroup = new Map<string, Map<string, number>>();
    energyData.forEach((item) => {
      const campaignGroups = groupByCampaign(item.timeSeries);
      const totals = new Map<string, number>();
      campaignGroups.forEach((ms, c) => {
        allCampaigns.add(c);
        totals.set(c, ms.reduce((s, t) => s + t.valeur, 0));
      });
      energyCampByGroup.set(item.groupName, totals);
    });

    // Campaign totals for ha
    const haCampByGroup = new Map<string, Map<string, number>>();
    haData.forEach((item) => {
      const campaignGroups = groupByCampaign(item.timeSeries);
      const totals = new Map<string, number>();
      campaignGroups.forEach((ms, c) => {
        totals.set(c, ms.reduce((s, t) => s + t.valeur, 0));
      });
      haCampByGroup.set(item.groupName, totals);
    });

    const sortedCampaigns = Array.from(allCampaigns).sort();
    const datasets = energyData
      .map((item, i) => {
        const eCamp = energyCampByGroup.get(item.groupName)!;
        const haCamp = haCampByGroup.get(item.groupName);
        return {
          label: item.groupName,
          data: sortedCampaigns.map((c) => {
            const e = eCamp?.get(c) || 0;
            const ha = haCamp?.get(c) || 0;
            return ha > 0 ? e / ha : 0;
          }),
          backgroundColor: getGroupColor(item.groupName, colorMap, i) + "CC",
          borderRadius: i === energyData.length - 1 ? 6 : 0,
          borderSkipped: false as const,
        };
      })
      .filter((ds) => ds.data.some((v) => v > 0));

    if (datasets.length === 0) return null;
    return { labels: sortedCampaigns, datasets };
  } else {
    if (months.length === 0) return null;
    const datasets = energyData
      .map((item, i) => {
        const energyByMonth = new Map(item.timeSeries.map((t) => [t.month, t.valeur]));
        const haEntry = haLookup.get(item.groupName);
        return {
          label: item.groupName,
          data: months.map((m) => {
            const e = energyByMonth.get(m) || 0;
            const ha = haEntry?.monthMap.get(m) || 0;
            return ha > 0 ? e / ha : 0;
          }),
          backgroundColor: getGroupColor(item.groupName, colorMap, i) + "CC",
          borderRadius: i === energyData.length - 1 ? 6 : 0,
          borderSkipped: false as const,
        };
      })
      .filter((ds) => ds.data.some((v) => v > 0));

    if (datasets.length === 0) return null;
    return { labels: months, datasets };
  }
}

function buildHorizontalBarData(
  data: { siteName: string; total: number }[],
  label: string
) {
  if (data.length === 0) return null;
  return {
    labels: data.map((s) => s.siteName),
    datasets: [{
      label,
      data: data.map((s) => s.total),
      backgroundColor: "#37daafCC",
      borderRadius: 4,
      borderSkipped: false as const,
    }],
  };
}

const EnergyPage: React.FC<EnergyPageProps> = ({ user }) => {
  const navigate = useNavigate();
  const pageTitle = usePageTitle();
  const handleLogout = () => navigate("/");
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12

  // Determine current campaign (Oct–Sep)
  const currentCampaign = currentMonth >= 10 ? `${currentYear}/${(currentYear + 1) % 100}` : `${currentYear - 1}/${currentYear % 100}`;

  // Shared filter keys with Water module for connected filtering
  const [filterType, setFilterType] = usePersistedState<"months" | "campaigns">("shared-filterType", "months");
  const [fromPeriod, setFromPeriod] = usePersistedState("shared-fromPeriod", `${currentYear}-01`);
  const [toPeriod, setToPeriod] = usePersistedState("shared-toPeriod", `${currentYear}-12`);
  const [fromCampaign, setFromCampaign] = usePersistedState("shared-fromCampaign", currentCampaign);
  const [toCampaign, setToCampaign] = usePersistedState("shared-toCampaign", currentCampaign);

  // Helper: Convert campaign (e.g., "23/24") to date range
  const campaignToMonths = (campaign: string): { start: string; end: string } => {
    const [startYear, endYear] = campaign.split('/').map(y => 2000 + parseInt(y));
    return {
      start: `${startYear}-10`, // October of start year
      end: `${endYear}-09`,     // September of end year
    };
  };

  // Calculate effective periods based on filter type
  const effectivePeriods = useMemo(() => {
    if (filterType === "campaigns") {
      const fromRange = campaignToMonths(fromCampaign);
      const toRange = campaignToMonths(toCampaign);
      return {
        from_period: fromRange.start,
        to_period: toRange.end,
      };
    }
    return {
      from_period: fromPeriod,
      to_period: toPeriod,
    };
  }, [filterType, fromPeriod, toPeriod, fromCampaign, toCampaign]);

  // Generate list of available campaigns (last 10 years)
  const availableCampaigns = useMemo(() => {
    const campaigns: string[] = [];
    const startYear = currentYear - 5;
    const endYear = currentYear + 5;
    for (let year = startYear; year <= endYear; year++) {
      campaigns.push(`${year % 100}/${(year + 1) % 100}`);
    }
    return campaigns;
  }, [currentYear]);

  // Generate list of available months (5 years back and forward)
  const availableMonths = useMemo(() => {
    const months: { value: string; label: string }[] = [];
    const monthNames = [
      "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
      "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
    ];
    const startYear = currentYear - 5;
    const endYear = currentYear + 5;
    for (let year = startYear; year <= endYear; year++) {
      for (let month = 1; month <= 12; month++) {
        const value = `${year}-${String(month).padStart(2, '0')}`;
        const label = `${monthNames[month - 1]} ${year}`;
        months.push({ value, label });
      }
    }
    return months;
  }, [currentYear]);

  const [selectedSites, setSelectedSites] = usePersistedState<(number | string)[]>(
    "shared-sites",
    []
  );
  const [selectedBUs, setSelectedBUs] = usePersistedState<(number | string)[]>("shared-bus", []);
  const [selectedActivites, setSelectedActivites] = usePersistedState<(number | string)[]>(
    "shared-activites",
    []
  );
  const [selectedFilieres, setSelectedFilieres] = usePersistedState<(number | string)[]>(
    "shared-filieres",
    []
  );
  const [selectedRegions, setSelectedRegions] = usePersistedState<(number | string)[]>(
    "shared-regions",
    []
  );

  // Energy type filter
  const [selectedEnergyTypes, setSelectedEnergyTypes] = usePersistedState<(number | string)[]>(
    "energy-types",
    []
  );

  const ENERGY_TYPE_OPTIONS = useMemo(() => [
    { value: "ELEC", label: "Électricité" },
    { value: "GASOIL", label: "Gasoil" },
    { value: "ESSENCE", label: "Essence" },
    { value: "FUEL", label: "Fuel" },
    { value: "AUTRES", label: "Autres" },
  ], []);

  // Determine which energy types are active (all if none selected)
  const activeEnergyTypes = useMemo(() => {
    if (selectedEnergyTypes.length === 0) return ["ELEC", "GASOIL", "ESSENCE", "FUEL", "AUTRES"];
    return selectedEnergyTypes.map(String);
  }, [selectedEnergyTypes]);

  // Expandable panel states (all open by default)
  const [panelActivite, setPanelActivite] = useState(true);
  const [panelRegion, setPanelRegion] = useState(true);
  const [panelFiliere, setPanelFiliere] = useState(true);
  const [panelDetailType, setPanelDetailType] = useState(true);
  // Tracks which per-type sub-panels are open (true = open by default)
  const [openTypePanels, setOpenTypePanels] = useState<Record<string, boolean>>({});
  // Which energy types to display in the detailed section (ELEC selected by default)
  const [selectedDetailTypes, setSelectedDetailTypes] = useState<string[]>(["ELEC"]);

  const { data: sites = [] } = useGetSites();
  const { data: bus = [] } = useGetBUs();
  const { data: activites = [] } = useGetActivites();
  const { data: filieres = [] } = useGetFilieres();
  const { data: regions = [] } = useGetRegions();

  const { data: postesEmission = [] } = useGetPostesEmission();
  const energiePoste = postesEmission.find((poste) => poste.name.toLowerCase().includes("energie"));

  const { data: champsMetier = [] } = useGetPosteChampsMetier({
    posteId: energiePoste?.id || 0,
    enabled: !!energiePoste?.id,
  });

  // Auto-detect champ_metier IDs (no user selection needed)
  const champUniteId = useMemo(
    () => champsMetier.find((c) => c.code === "TOTAL_Unité")?.id ?? null,
    [champsMetier]
  );
  const champDHId = useMemo(
    () => champsMetier.find((c) => c.code === "TOTAL_DH")?.id ?? null,
    [champsMetier]
  );

  // Common filter values - convert to number arrays
  const siteIds = selectedSites.length > 0 ? selectedSites.map(id => Number(id)) : undefined;
  const buIds = selectedBUs.length > 0 ? selectedBUs.map(id => Number(id)) : undefined;
  const activiteIds = selectedActivites.length > 0 ? selectedActivites.map(id => Number(id)) : undefined;
  const filiereIds = selectedFilieres.length > 0 ? selectedFilieres.map(id => Number(id)) : undefined;
  const regionIds = selectedRegions.length > 0 ? selectedRegions.map(id => Number(id)) : undefined;

  // Energy KPIs from /core/analytics/energie/ (no champ_metier needed)
  const {
    data: energieKpisData,
    isLoading: kpisLoading,
  } = useGetEnergieKpis({
    from_period: effectivePeriods.from_period,
    to_period: effectivePeriods.to_period,
    site_ids: siteIds,
    bu_ids: buIds,
    activite_ids: activiteIds,
    filiere_ids: filiereIds,
    region_ids: regionIds,
  });

  // Extract individual energy KPIs
  const elecKpi = energieKpisData?.summary ? getKpiByCode(energieKpisData.summary, ENERGIE_KPI_CODES.ELEC) : undefined;
  const gasoilKpi = energieKpisData?.summary ? getKpiByCode(energieKpisData.summary, ENERGIE_KPI_CODES.GASOIL) : undefined;
  const essenceKpi = energieKpisData?.summary ? getKpiByCode(energieKpisData.summary, ENERGIE_KPI_CODES.ESSENCE) : undefined;
  const fuelKpi = energieKpisData?.summary ? getKpiByCode(energieKpisData.summary, ENERGIE_KPI_CODES.FUEL) : undefined;
  const autresKpi = energieKpisData?.summary ? getKpiByCode(energieKpisData.summary, ENERGIE_KPI_CODES.AUTRES) : undefined;

  // Calculate total TEP from active energy types only
  const totalTep =
    (activeEnergyTypes.includes("ELEC") ? (elecKpi?.total || 0) : 0) +
    (activeEnergyTypes.includes("GASOIL") ? (gasoilKpi?.total || 0) : 0) +
    (activeEnergyTypes.includes("ESSENCE") ? (essenceKpi?.total || 0) : 0) +
    (activeEnergyTypes.includes("FUEL") ? (fuelKpi?.total || 0) : 0) +
    (activeEnergyTypes.includes("AUTRES") ? (autresKpi?.total || 0) : 0);

  // ============================================
  // Name maps + ID lists
  // ============================================
  const activiteNamesMap = useMemo(() => {
    const map: Record<number, string> = {};
    activites.forEach((a) => { map[a.id] = a.name; });
    return map;
  }, [activites]);

  const perActiviteIds = useMemo(() => {
    if (selectedActivites.length > 0) return selectedActivites.map(Number);
    return activites.map((a) => a.id);
  }, [selectedActivites, activites]);

  const buNamesMap = useMemo(() => {
    const map: Record<number, string> = {};
    bus.forEach((b) => { map[b.id] = b.name; });
    return map;
  }, [bus]);

  const perBUIds = useMemo(() => {
    if (selectedBUs.length > 0) return selectedBUs.map(Number);
    return bus.map((b) => b.id);
  }, [selectedBUs, bus]);

  const siteNamesMap = useMemo(() => {
    const map: Record<number, string> = {};
    sites.forEach((s) => { map[s.id] = s.name; });
    return map;
  }, [sites]);

  const perSiteIds = useMemo(() => {
    if (selectedSites.length > 0) return selectedSites.map(Number);
    return sites.map((s) => s.id);
  }, [selectedSites, sites]);

  const regionNamesMap = useMemo(() => {
    const map: Record<number, string> = {};
    regions.forEach((r) => { map[r.id] = r.name; });
    return map;
  }, [regions]);

  const perRegionIds = useMemo(() => {
    if (selectedRegions.length > 0) return selectedRegions.map(Number);
    return regions.map((r) => r.id);
  }, [selectedRegions, regions]);

  const filiereNamesMap = useMemo(() => {
    const map: Record<number, string> = {};
    filieres.forEach((f) => { map[f.id] = f.name; });
    return map;
  }, [filieres]);

  const perFiliereIds = useMemo(() => {
    return filieres.map((f) => f.id);
  }, [filieres]);

  // ============================================
  // HOOKS: TEP (champ_metier = TOTAL_Unité)
  // ============================================
  const { groupData: activiteDataTep, isLoading: activiteTepLoading } = useGetPerGroupAnalytics({
    baseFilters: { from_period: effectivePeriods.from_period, to_period: effectivePeriods.to_period, site_ids: siteIds, bu_ids: buIds, filiere_ids: filiereIds, region_ids: regionIds, champ_metier: champUniteId },
    groupKey: "activite_id",
    groupIds: perActiviteIds,
    groupNames: activiteNamesMap,
    module: "energie",
    enabled: !!champUniteId,
  });

  const { groupData: buDataTep, allMonths: buMonthsTep, isLoading: buTepLoading } = useGetPerGroupAnalytics({
    baseFilters: { from_period: effectivePeriods.from_period, to_period: effectivePeriods.to_period, site_ids: siteIds, activite_ids: activiteIds, filiere_ids: filiereIds, region_ids: regionIds, champ_metier: champUniteId },
    groupKey: "bu_id",
    groupIds: perBUIds,
    groupNames: buNamesMap,
    module: "energie",
    enabled: !!champUniteId,
  });

  const { groupData: regionDataTep, allMonths: regionMonthsTep, isLoading: regionTepLoading } = useGetPerGroupAnalytics({
    baseFilters: { from_period: effectivePeriods.from_period, to_period: effectivePeriods.to_period, site_ids: siteIds, bu_ids: buIds, activite_ids: activiteIds, filiere_ids: filiereIds, champ_metier: champUniteId },
    groupKey: "region_id",
    groupIds: perRegionIds,
    groupNames: regionNamesMap,
    module: "energie",
    enabled: !!champUniteId,
  });

  const { groupData: filiereDataTep, allMonths: filiereMonthsTep, isLoading: filiereTepLoading } = useGetPerGroupAnalytics({
    baseFilters: { from_period: effectivePeriods.from_period, to_period: effectivePeriods.to_period, site_ids: siteIds, bu_ids: buIds, activite_ids: activiteIds, region_ids: regionIds, champ_metier: champUniteId },
    groupKey: "filiere_id",
    groupIds: perFiliereIds,
    groupNames: filiereNamesMap,
    module: "energie",
    enabled: !!champUniteId,
  });

  const { siteData: siteDataTep } = useGetPerSiteAnalytics({
    baseFilters: { from_period: effectivePeriods.from_period, to_period: effectivePeriods.to_period, bu_ids: buIds, activite_ids: activiteIds, filiere_ids: filiereIds, region_ids: regionIds, champ_metier: champUniteId },
    siteIds: perSiteIds,
    siteNames: siteNamesMap,
    module: "energie",
    enabled: !!champUniteId,
  });

  // ============================================
  // HOOKS: DH (champ_metier = TOTAL_DH)
  // ============================================
  const { groupData: activiteDataDh, isLoading: activiteDhLoading } = useGetPerGroupAnalytics({
    baseFilters: { from_period: effectivePeriods.from_period, to_period: effectivePeriods.to_period, site_ids: siteIds, bu_ids: buIds, filiere_ids: filiereIds, region_ids: regionIds, champ_metier: champDHId },
    groupKey: "activite_id",
    groupIds: perActiviteIds,
    groupNames: activiteNamesMap,
    module: "energie",
    enabled: !!champDHId,
  });

  const { groupData: buDataDh, allMonths: buMonthsDh, isLoading: buDhLoading } = useGetPerGroupAnalytics({
    baseFilters: { from_period: effectivePeriods.from_period, to_period: effectivePeriods.to_period, site_ids: siteIds, activite_ids: activiteIds, filiere_ids: filiereIds, region_ids: regionIds, champ_metier: champDHId },
    groupKey: "bu_id",
    groupIds: perBUIds,
    groupNames: buNamesMap,
    module: "energie",
    enabled: !!champDHId,
  });

  const { groupData: regionDataDh, allMonths: regionMonthsDh, isLoading: regionDhLoading } = useGetPerGroupAnalytics({
    baseFilters: { from_period: effectivePeriods.from_period, to_period: effectivePeriods.to_period, site_ids: siteIds, bu_ids: buIds, activite_ids: activiteIds, filiere_ids: filiereIds, champ_metier: champDHId },
    groupKey: "region_id",
    groupIds: perRegionIds,
    groupNames: regionNamesMap,
    module: "energie",
    enabled: !!champDHId,
  });

  const { groupData: filiereDataDh, allMonths: filiereMonthsDh, isLoading: filiereDhLoading } = useGetPerGroupAnalytics({
    baseFilters: { from_period: effectivePeriods.from_period, to_period: effectivePeriods.to_period, site_ids: siteIds, bu_ids: buIds, activite_ids: activiteIds, region_ids: regionIds, champ_metier: champDHId },
    groupKey: "filiere_id",
    groupIds: perFiliereIds,
    groupNames: filiereNamesMap,
    module: "energie",
    enabled: !!champDHId,
  });

  const { siteData: siteDataDh } = useGetPerSiteAnalytics({
    baseFilters: { from_period: effectivePeriods.from_period, to_period: effectivePeriods.to_period, bu_ids: buIds, activite_ids: activiteIds, filiere_ids: filiereIds, region_ids: regionIds, champ_metier: champDHId },
    siteIds: perSiteIds,
    siteNames: siteNamesMap,
    module: "energie",
    enabled: !!champDHId,
  });

  // ============================================
  // HOOKS: Raw energy per type per BU (native units via selections)
  // ============================================

  // Fetch energie_type dimension and its valeurs (ELEC, GASOIL, ESSENCE, FUEL, etc.)
  const { data: energieDimensions = [] } = useGetPosteDimensions({
    posteId: energiePoste?.id || 0,
    enabled: !!energiePoste?.id,
  });

  const energieTypeDimension = useMemo(
    () => energieDimensions.find((d) => d.code === "energie_type") ?? null,
    [energieDimensions]
  );

  // Production poste → SUP_ha champ (for per-ha normalization)
  const productionPoste = useMemo(
    () => postesEmission.find((p) => p.name.toLowerCase().includes("production")),
    [postesEmission]
  );
  const { data: productionChampsMetier = [] } = useGetPosteChampsMetier({
    posteId: productionPoste?.id || 0,
    enabled: !!productionPoste?.id,
  });
  const supHaId = useMemo(
    () => productionChampsMetier.find((c) => c.code === "SUP_ha")?.id ?? null,
    [productionChampsMetier]
  );

  // SUP_ha per BU (denominator for per-ha charts)
  const { groupData: buDataSupHa } = useGetPerGroupAnalytics({
    baseFilters: {
      from_period: effectivePeriods.from_period,
      to_period: effectivePeriods.to_period,
      site_ids: siteIds,
      activite_ids: activiteIds,
      filiere_ids: filiereIds,
      region_ids: regionIds,
      champ_metier: supHaId,
    },
    groupKey: "bu_id",
    groupIds: perBUIds,
    groupNames: buNamesMap,
    module: "energie",
    enabled: !!supHaId,
  });

  // Raw energy per dimension value per BU
  const { dataByType: rawEnergieByType, isLoading: rawTypesLoading } = useGetRawEnergiePerTypeBU({
    baseFilters: {
      from_period: effectivePeriods.from_period,
      to_period: effectivePeriods.to_period,
      site_ids: siteIds,
      activite_ids: activiteIds,
      filiere_ids: filiereIds,
      region_ids: regionIds,
    },
    champMetierId: champUniteId,
    energieDimension: energieTypeDimension,
    perBUIds,
    buNamesMap,
    enabled: !!champUniteId && !!energieTypeDimension,
  });

  // Solar production from /core/saisie-analytics/ — returns raw kWh (native unit)
  const { data: solarSaisieData } = useGetModuleAnalytics({
    filters: {
      from_period: effectivePeriods.from_period,
      to_period: effectivePeriods.to_period,
      site_ids: siteIds,
      bu_ids: buIds,
      activite_ids: activiteIds,
      filiere_ids: filiereIds,
      region_ids: regionIds,
      champ_metier: champUniteId,
      selections: energieTypeDimension ? [{ dimension_id: energieTypeDimension.id, valeur_id: 8 }] : undefined,
    },
    enabled: !!champUniteId && !!energieTypeDimension,
  });

  // Separate known types from AUTRES, then aggregate AUTRES per BU
  const autresGroupsData = useMemo(() => {
    const autresEntries = Object.entries(rawEnergieByType).filter(
      ([code]) => !KNOWN_INDIVIDUAL_CODES.includes(code.toUpperCase())
    );
    if (autresEntries.length === 0) return { groups: [] as RawEnergieGroupEntry[], allMonths: [] as string[] };

    const buAggr: Record<string, { groupId: number; groupName: string; total: number; monthMap: Map<string, number> }> = {};
    for (const [, typeData] of autresEntries) {
      for (const group of typeData.groups) {
        if (!buAggr[group.groupName]) {
          buAggr[group.groupName] = { groupId: group.groupId, groupName: group.groupName, total: 0, monthMap: new Map() };
        }
        buAggr[group.groupName].total += group.total;
        for (const ts of group.timeSeries) {
          buAggr[group.groupName].monthMap.set(ts.month, (buAggr[group.groupName].monthMap.get(ts.month) || 0) + ts.valeur);
        }
      }
    }
    const groups = Object.values(buAggr)
      .filter((g) => g.total > 0)
      .map((g) => ({
        groupId: g.groupId,
        groupName: g.groupName,
        total: g.total,
        timeSeries: Array.from(g.monthMap.entries())
          .map(([month, valeur]) => ({ month, valeur }))
          .sort((a, b) => a.month.localeCompare(b.month)),
      }))
      .sort((a, b) => b.total - a.total);
    const allMonths = Array.from(new Set(groups.flatMap((g) => g.timeSeries.map((t) => t.month)))).sort();
    return { groups, allMonths };
  }, [rawEnergieByType]);

  // Find type data with flexible code matching (handles ELEC vs ELECTRICITE, etc.)
  const findTypeGroups = useMemo(() => {
    return (targetCode: string): { groups: RawEnergieGroupEntry[]; allMonths: string[] } => {
      const upper = targetCode.toUpperCase();
      const entry = Object.entries(rawEnergieByType).find(([code]) => {
        const c = code.toUpperCase();
        return c === upper || c.includes(upper) || upper.includes(c);
      });
      return entry ? { groups: entry[1].groups, allMonths: entry[1].allMonths } : { groups: [], allMonths: [] };
    };
  }, [rawEnergieByType]);

  // --- Cascading filter logic ---
  // Filter sites based on all selected organizational entities
  const filteredSites = useMemo(() => {
    return sites.filter((site) => {
      if (selectedBUs.length > 0 && !selectedBUs.includes(site.bu)) return false;
      if (selectedActivites.length > 0 && !selectedActivites.includes(site.activite)) return false;
      if (selectedFilieres.length > 0 && !selectedFilieres.includes(site.filiere)) return false;
      if (selectedRegions.length > 0 && !selectedRegions.includes(site.region)) return false;
      return true;
    });
  }, [sites, selectedBUs, selectedActivites, selectedFilieres, selectedRegions]);

  // Get unique entity IDs from filtered sites for cascading dropdowns
  const availableBUIds = useMemo(() => {
    return new Set(sites.map((s) => s.bu));
  }, [sites]);

  const availableActiviteIds = useMemo(() => {
    let sitesToCheck = sites;
    if (selectedBUs.length > 0) {
      sitesToCheck = sitesToCheck.filter((s) => selectedBUs.includes(s.bu));
    }
    return new Set(sitesToCheck.map((s) => s.activite));
  }, [sites, selectedBUs]);

  const availableFiliereIds = useMemo(() => {
    let sitesToCheck = sites;
    if (selectedBUs.length > 0) {
      sitesToCheck = sitesToCheck.filter((s) => selectedBUs.includes(s.bu));
    }
    if (selectedActivites.length > 0) {
      sitesToCheck = sitesToCheck.filter((s) => selectedActivites.includes(s.activite));
    }
    return new Set(sitesToCheck.map((s) => s.filiere));
  }, [sites, selectedBUs, selectedActivites]);

  const availableRegionIds = useMemo(() => {
    let sitesToCheck = sites;
    if (selectedBUs.length > 0) {
      sitesToCheck = sitesToCheck.filter((s) => selectedBUs.includes(s.bu));
    }
    if (selectedActivites.length > 0) {
      sitesToCheck = sitesToCheck.filter((s) => selectedActivites.includes(s.activite));
    }
    if (selectedFilieres.length > 0) {
      sitesToCheck = sitesToCheck.filter((s) => selectedFilieres.includes(s.filiere));
    }
    return new Set(sitesToCheck.map((s) => s.region));
  }, [sites, selectedBUs, selectedActivites, selectedFilieres]);

  // --- Dropdown items (filtered based on hierarchy) ---
  const buItems = useMemo(
    () => bus.filter((b) => availableBUIds.has(b.id)).map((b) => ({ value: b.id, label: `${b.code} - ${b.name}` })),
    [bus, availableBUIds]
  );

  const activiteItems = useMemo(
    () => activites.filter((a) => availableActiviteIds.has(a.id)).map((a) => ({ value: a.id, label: `${a.code} - ${a.name}` })),
    [activites, availableActiviteIds]
  );

  const filiereItems = useMemo(
    () => filieres.filter((f) => availableFiliereIds.has(f.id)).map((f) => ({ value: f.id, label: `${f.code} - ${f.name}` })),
    [filieres, availableFiliereIds]
  );

  const regionItems = useMemo(
    () => regions.filter((r) => availableRegionIds.has(r.id)).map((r) => ({ value: r.id, label: `${r.code} - ${r.name}` })),
    [regions, availableRegionIds]
  );

  const siteItems = useMemo(
    () => filteredSites.map((s) => ({ value: s.id, label: s.name })),
    [filteredSites]
  );

  // ============================================
  // CHART DATA: Evolution par Type (TEP) - from KPI time series
  // ============================================
  const tepByTypeBarData = useMemo(() => {
    const ts = energieKpisData?.time_series;
    if (!ts?.length) return null;

    const ALL_TYPES = [
      { key: ENERGIE_KPI_CODES.ELEC, label: "ELEC", typeKey: "ELEC", color: ENERGY_TYPE_COLORS.ELEC },
      { key: ENERGIE_KPI_CODES.GASOIL, label: "GASOIL", typeKey: "GASOIL", color: ENERGY_TYPE_COLORS.GASOIL },
      { key: ENERGIE_KPI_CODES.ESSENCE, label: "ESSENCE", typeKey: "ESSENCE", color: ENERGY_TYPE_COLORS.ESSENCE },
      { key: ENERGIE_KPI_CODES.FUEL, label: "FUEL", typeKey: "FUEL", color: ENERGY_TYPE_COLORS.FUEL },
      { key: ENERGIE_KPI_CODES.AUTRES, label: "AUTRES", typeKey: "AUTRES", color: ENERGY_TYPE_COLORS.AUTRES },
    ];

    // Filter by selected energy types
    const TYPES = ALL_TYPES.filter(t => activeEnergyTypes.includes(t.typeKey));
    if (TYPES.length === 0) return null;

    if (filterType === "campaigns") {
      // Group by campaign and sum values
      const campaignGroups = groupByCampaign(ts);
      const campaigns: string[] = [];
      const campaignData = new Map<string, number[]>();

      // Initialize data structure
      TYPES.forEach(type => campaignData.set(type.key, []));

      // Process each campaign
      campaignGroups.forEach((monthsInCampaign, campaign) => {
        campaigns.push(campaign);

        TYPES.forEach(type => {
          const total = monthsInCampaign.reduce((sum, t) => sum + Number(t[type.key] || 0), 0);
          campaignData.get(type.key)!.push(total);
        });
      });

      // Sort campaigns chronologically
      const sortedIndices = campaigns
        .map((c, i) => ({ campaign: c, index: i }))
        .sort((a, b) => a.campaign.localeCompare(b.campaign))
        .map(x => x.index);

      const sortedCampaigns = sortedIndices.map(i => campaigns[i]);
      const sortedData = new Map<string, number[]>();
      TYPES.forEach(type => {
        sortedData.set(type.key, sortedIndices.map(i => campaignData.get(type.key)![i]));
      });

      return {
        labels: sortedCampaigns,
        datasets: TYPES.map((type, i) => ({
          label: type.label,
          data: sortedData.get(type.key)!,
          backgroundColor: type.color + "CC",
          borderRadius: i === TYPES.length - 1 ? 6 : 0,
          borderSkipped: false as const,
        })),
      };
    } else {
      // Monthly data
      const months = ts.map((t) => t.month);

      return {
        labels: months,
        datasets: TYPES.map((type, i) => ({
          label: type.label,
          data: months.map((m) => {
            const entry = ts.find((t) => t.month === m);
            return entry ? Number(entry[type.key] || 0) : 0;
          }),
          backgroundColor: type.color + "CC",
          borderRadius: i === TYPES.length - 1 ? 6 : 0,
          borderSkipped: false as const,
        })),
      };
    }
  }, [energieKpisData, filterType, activeEnergyTypes]);

  // ============================================
  // CHART DATA: Conso Électricité + Production PV (GWh) + Taux Autoconso
  // Elec: TEP × 11630 / 1 000 000 → GWh (from energieKpisData)
  // Solar: kWh / 1 000 000 → GWh (from solarSaisieData via /core/saisie-analytics/)
  // Taux Autoconso (%) = solar / (elec + solar) × 100
  // ============================================
  const consoGWhBarData = useMemo(() => {
    const ts = energieKpisData?.time_series;
    if (!ts?.length) return null;

    // Build month → kWh map from saisie-analytics solar data
    const solarKwhMap = new Map<string, number>();
    solarSaisieData?.time_series?.forEach(entry => {
      solarKwhMap.set(entry.month, entry.valeur);
    });

    const getSolarKWh = (month: string): number => solarKwhMap.get(month) ?? 0;

    let labels: string[];
    let elecGWh: number[];
    let solarGWh: number[];

    if (filterType === "campaigns") {
      const campaignGroups = groupByCampaign(ts);
      const pairs: { l: string; elec: number; solar: number }[] = [];
      campaignGroups.forEach((months, campaign) => {
        const elec = months.reduce((s, t) => s + Number(t[ENERGIE_KPI_CODES.ELEC] || 0), 0) * TEP_TO_GWH;
        const solar = months.reduce((s, t) => s + getSolarKWh(t.month as string), 0) * KWH_TO_GWH;
        pairs.push({ l: campaign, elec, solar });
      });
      pairs.sort((a, b) => a.l.localeCompare(b.l));
      labels = pairs.map(p => p.l);
      elecGWh = pairs.map(p => p.elec);
      solarGWh = pairs.map(p => p.solar);
    } else {
      labels = ts.map(t => t.month as string);
      elecGWh = ts.map(t => Number(t[ENERGIE_KPI_CODES.ELEC] || 0) * TEP_TO_GWH);
      solarGWh = ts.map(t => getSolarKWh(t.month as string) * KWH_TO_GWH);
    }

    const tauxAutoconso = elecGWh.map((elec, i) => {
      const solar = solarGWh[i];
      const total = elec + solar;
      return total > 0 ? (solar / total) * 100 : 0;
    });

    return {
      labels,
      datasets: [
        {
          type: "bar" as const,
          label: "Conso Élec (GWh)",
          data: elecGWh,
          backgroundColor: ACCENT + "CC",
          borderRadius: 6,
          borderSkipped: false as const,
          yAxisID: "y",
          order: 2,
        },
        {
          type: "bar" as const,
          label: "Production PV (GWh)",
          data: solarGWh,
          backgroundColor: "#22c55eCC",
          borderRadius: 6,
          borderSkipped: false as const,
          yAxisID: "y",
          order: 2,
        },
        {
          type: "line" as const,
          label: "Taux Autoconso (%)",
          data: tauxAutoconso,
          borderColor: "#0f172a",
          backgroundColor: "transparent",
          borderWidth: 2,
          borderDash: [6, 3],
          pointBackgroundColor: "#0f172a",
          pointRadius: 5,
          pointHoverRadius: 7,
          tension: 0,
          yAxisID: "y2",
          order: 1,
        },
      ],
    };
  }, [energieKpisData, solarSaisieData, filterType]);

  // ============================================
  // CHART DATA: TEP versions
  // ============================================
  const activitePieTep = useMemo(() => buildPieData(activiteDataTep, ACTIVITE_COLORS), [activiteDataTep]);
  const buPieTep = useMemo(() => buildPieData(buDataTep, BU_COLORS), [buDataTep]);
  const buBarTep = useMemo(() => buildStackedBarData(buDataTep, buMonthsTep, BU_COLORS, filterType === "campaigns"), [buDataTep, buMonthsTep, filterType]);
  const regionBarTep = useMemo(() => buildStackedBarData(regionDataTep, regionMonthsTep, {}, filterType === "campaigns"), [regionDataTep, regionMonthsTep, filterType]);

  // Top-N selector for site ranking charts. 0 means show all.
  const [siteRankingTopN, setSiteRankingTopN] = usePersistedState<number>("energy-site-ranking-topN", 10);

  const siteRankTep = useMemo(() => {
    if (!siteDataTep || siteDataTep.length === 0) return null;
    const slice = siteRankingTopN > 0 ? siteDataTep.slice(0, siteRankingTopN) : siteDataTep;
    return buildHorizontalBarData(slice, "Consommation (TEP)");
  }, [siteDataTep, siteRankingTopN]);
  const filierePieTep = useMemo(() => buildPieData(filiereDataTep, FILIERE_COLORS), [filiereDataTep]);
  const filiereBarTep = useMemo(() => buildStackedBarData(filiereDataTep, filiereMonthsTep, FILIERE_COLORS, filterType === "campaigns"), [filiereDataTep, filiereMonthsTep, filterType]);

  // ============================================
  // CHART DATA: DH versions
  // ============================================
  const activitePieDh = useMemo(() => buildPieData(activiteDataDh, ACTIVITE_COLORS), [activiteDataDh]);
  const buPieDh = useMemo(() => buildPieData(buDataDh, BU_COLORS), [buDataDh]);
  const buBarDh = useMemo(() => buildStackedBarData(buDataDh, buMonthsDh, BU_COLORS, filterType === "campaigns"), [buDataDh, buMonthsDh, filterType]);
  const regionBarDh = useMemo(() => buildStackedBarData(regionDataDh, regionMonthsDh, {}, filterType === "campaigns"), [regionDataDh, regionMonthsDh, filterType]);

  const siteRankDh = useMemo(() => {
    if (!siteDataDh || siteDataDh.length === 0) return null;
    const slice = siteRankingTopN > 0 ? siteDataDh.slice(0, siteRankingTopN) : siteDataDh;
    return buildHorizontalBarData(slice, "Charges (DH)");
  }, [siteDataDh, siteRankingTopN]);
  const filierePieDh = useMemo(() => buildPieData(filiereDataDh, FILIERE_COLORS), [filiereDataDh]);
  const filiereBarDh = useMemo(() => buildStackedBarData(filiereDataDh, filiereMonthsDh, FILIERE_COLORS, filterType === "campaigns"), [filiereDataDh, filiereMonthsDh, filterType]);

  // ============================================
  // Common chart options
  // ============================================
  const stackedBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "top" as const,
          labels: {
            usePointStyle: true,
            pointStyle: "circle" as const,
            font: { size: 10 },
            color: "#475569",
            padding: 12,
            boxWidth: 8,
          },
          onClick: legendIsolateClickDataset,
        },
        tooltip: {
          backgroundColor: "#1e293b",
          titleFont: { size: 13, weight: "bold" as const },
          bodyFont: { size: 12 },
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (ctx: any) =>
              `${ctx.dataset.label}: ${formatLargeNumber(ctx.parsed.y ?? 0)}`,
            footer: (items: any[]) => {
              const total = items.reduce((s: number, i: any) => s + (i.parsed.y ?? 0), 0);
              return `Total: ${formatLargeNumber(total)}`;
            },
          },
        },
        datalabels: {
          display: (ctx: any) => ctx.datasetIndex === ctx.chart.data.datasets.length - 1,
          anchor: "end" as const,
          align: "top" as const,
          formatter: (_value: number, ctx: any) => {
            const datasets = ctx.chart.data.datasets;
            let total = 0;
            for (const ds of datasets) {
              total += ds.data[ctx.dataIndex] ?? 0;
            }
            return formatLargeNumber(total);
          },
          color: "#334155",
          font: { size: 10, weight: "bold" as const },
        },
      },
      scales: {
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: "#f1f5f9" },
          ticks: {
            color: "#94a3b8",
            font: { size: 11 },
            callback: (value: string | number) => formatLargeNumber(Number(value)),
          },
        },
        x: {
          stacked: true,
          grid: { display: false },
          ticks: { color: "#94a3b8", font: { size: 11 } },
        },
      },
    }),
    []
  );

  const pieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "55%",
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: {
            padding: 16,
            usePointStyle: true,
            pointStyle: "circle" as const,
            font: { size: 11, family: "Arial Black" },
            color: "#475569",
          },
          onClick: legendIsolateClickPie,
        },
        tooltip: {
          backgroundColor: "#1e293b",
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (ctx: any) => {
              const val = ctx.parsed;
              const allData = ctx.dataset.data as number[];
              const pieTotal = allData.reduce((s: number, v: number) => s + v, 0);
              const pct = pieTotal > 0 ? ((val / pieTotal) * 100).toFixed(1) : "0";
              return `${formatLargeNumber(val)} | ${pct}%`;
            },
          },
        },
        datalabels: {
          display: (ctx: any) => {
            const allData = ctx.dataset.data as number[];
            const pieTotal = allData.reduce((s: number, v: number) => s + v, 0);
            return pieTotal > 0 && (allData[ctx.dataIndex] / pieTotal) * 100 >= 7;
          },
          formatter: (value: number, ctx: any) => {
            const allData = ctx.dataset.data as number[];
            const pieTotal = allData.reduce((s: number, v: number) => s + v, 0);
            const pct = pieTotal > 0 ? ((value / pieTotal) * 100).toFixed(1) : "0";
            return `${pct}%`;
          },
          color: "#fff",
          font: { size: 14, weight: "bold" as const },
          textShadowColor: "rgba(0,0,0,0.4)",
          textShadowBlur: 3,
        },
      },
    }),
    []
  );

  const horizontalBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y" as const,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#1e293b",
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (ctx: any) => formatLargeNumber(ctx.parsed.x ?? 0),
          },
        },
        datalabels: {
          display: true,
          anchor: "end" as const,
          align: "right" as const,
          formatter: (value: number) => formatLargeNumber(value),
          color: "#334155",
          font: { size: 11, weight: "bold" as const },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: { color: "#f1f5f9" },
          ticks: {
            color: "#94a3b8",
            font: { size: 11 },
            callback: (value: string | number) => formatLargeNumber(Number(value)),
          },
        },
        y: {
          grid: { display: false },
          ticks: { color: "#475569", font: { size: 11 } },
        },
      },
    }),
    []
  );

  const anyLoading =
    activiteTepLoading || activiteDhLoading ||
    buTepLoading || buDhLoading ||
    regionTepLoading || regionDhLoading ||
    filiereTepLoading || filiereDhLoading;

  const hasAnyChartData =
    activiteDataTep.length > 0 || activiteDataDh.length > 0 ||
    buDataTep.length > 0 || buDataDh.length > 0 ||
    regionDataTep.length > 0 || regionDataDh.length > 0 ||
    filiereDataTep.length > 0 || filiereDataDh.length > 0;

  return (
    <div className="energy-page">
      <Topbar
        title={pageTitle}
        userName={user.first_name || user.username}
        onLogout={handleLogout}
        user={user}
        sites={sites}
      />

      <div className="energy-content">
        {/* Header */}
        <div className="energy-header">
          <div className="energy-header-left">
            <div className="energy-icon-wrapper" style={{ background: `${ACCENT}20` }}>
              <Zap size={28} style={{ color: ACCENT }} strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="energy-title">Gestion de l'{"\u00C9"}nergie</h1>
              <p className="energy-subtitle">
                Analyse et visualisation de la consommation {"\u00e9"}nerg{"\u00e9"}tique
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="energy-card energy-filters">
          <div className="energy-filters-header">
            <Filter size={18} style={{ color: ACCENT }} />
            <span>Filtres et Param{"\u00e8"}tres</span>
          </div>
          <div className="energy-filters-grid">
            <div className="energy-filter-item">
              <label className="energy-label">Type de période</label>
              <div className="energy-period-toggle">
                {(["months", "campaigns"] as const).map((p) => (
                  <button
                    key={p}
                    className={`energy-toggle-btn${filterType === p ? " active" : ""}`}
                    onClick={() => setFilterType(p)}
                  >
                    {p === "campaigns" ? "Par Campagne" : "Par Mois"}
                  </button>
                ))}
              </div>
            </div>

            {filterType === "months" ? (
              <>
                <div className="energy-filter-item">
                  <label className="energy-label">
                    <Calendar size={14} style={{ color: ACCENT }} />P{"\u00e9"}riode de d{"\u00e9"}but
                  </label>
                  <select
                    value={fromPeriod}
                    onChange={(e) => {
                      const newFrom = e.target.value;
                      setFromPeriod(newFrom);
                      // Auto-update end period to match if it's before the new start
                      if (toPeriod < newFrom) {
                        setToPeriod(newFrom);
                      }
                    }}
                    className="energy-select"
                  >
                    {availableMonths.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="energy-filter-item">
                  <label className="energy-label">
                    <Calendar size={14} style={{ color: ACCENT }} />P{"\u00e9"}riode de fin
                  </label>
                  <select
                    value={toPeriod}
                    onChange={(e) => setToPeriod(e.target.value)}
                    className="energy-select"
                  >
                    {availableMonths.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="energy-filter-item">
                  <label className="energy-label">
                    <Calendar size={14} style={{ color: ACCENT }} />Campagne de d{"\u00e9"}but
                  </label>
                  <select
                    value={fromCampaign}
                    onChange={(e) => {
                      const newFrom = e.target.value;
                      setFromCampaign(newFrom);
                      // Auto-update end campaign to match if it's before the new start
                      const fromIndex = availableCampaigns.indexOf(newFrom);
                      const toIndex = availableCampaigns.indexOf(toCampaign);
                      if (toIndex < fromIndex) {
                        setToCampaign(newFrom);
                      }
                    }}
                    className="energy-select"
                  >
                    {availableCampaigns.map((camp) => (
                      <option key={camp} value={camp}>
                        {camp}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="energy-filter-item">
                  <label className="energy-label">
                    <Calendar size={14} style={{ color: ACCENT }} />Campagne de fin
                  </label>
                  <select
                    value={toCampaign}
                    onChange={(e) => setToCampaign(e.target.value)}
                    className="energy-select"
                  >
                    {availableCampaigns.map((camp) => (
                      <option key={camp} value={camp}>
                        {camp}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div className="energy-filter-item">
              <label className="energy-label">Site</label>
              <MultiSearchableCombobox
                items={siteItems}
                values={selectedSites}
                onChange={setSelectedSites}
                placeholder="Tous les sites"
                accentColor={ACCENT}
              />
            </div>
            <div className="energy-filter-item">
              <label className="energy-label">Business Unit</label>
              <MultiSearchableCombobox
                items={buItems}
                values={selectedBUs}
                onChange={setSelectedBUs}
                placeholder="Toutes les BU"
                accentColor={ACCENT}
              />
            </div>
            <div className="energy-filter-item">
              <label className="energy-label">Activit{"\u00e9"}</label>
              <MultiSearchableCombobox
                items={activiteItems}
                values={selectedActivites}
                onChange={setSelectedActivites}
                placeholder="Toutes les activités"
                accentColor={ACCENT}
              />
            </div>
            <div className="energy-filter-item">
              <label className="energy-label">Fili{"\u00e8"}re</label>
              <MultiSearchableCombobox
                items={filiereItems}
                values={selectedFilieres}
                onChange={setSelectedFilieres}
                placeholder="Toutes les filières"
                accentColor={ACCENT}
              />
            </div>
            <div className="energy-filter-item">
              <label className="energy-label">R{"\u00e9"}gion</label>
              <MultiSearchableCombobox
                items={regionItems}
                values={selectedRegions}
                onChange={setSelectedRegions}
                placeholder="Toutes les régions"
                accentColor={ACCENT}
              />
            </div>
            <div className="energy-filter-item">
              <label className="energy-label">Type d'{"\u00e9"}nergie</label>
              <MultiSearchableCombobox
                items={ENERGY_TYPE_OPTIONS}
                values={selectedEnergyTypes}
                onChange={setSelectedEnergyTypes}
                placeholder="Tous les types"
                accentColor={ACCENT}
              />
            </div>
          </div>
        </div>

        {/* Main KPIs - Energy Types */}
        <div className="energy-kpi-grid">
          <div className="energy-kpi" style={{ background: "linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)" }}>
            <div className="energy-kpi-content">
              <div>
                <p className="energy-kpi-label">Total {"\u00c9"}nergie</p>
                <p className="energy-kpi-value">{kpisLoading ? "..." : formatLargeNumber(totalTep)}</p>
                <p className="energy-kpi-unit">TEP</p>
              </div>
              <div className="energy-kpi-icon"><Zap size={26} /></div>
            </div>
          </div>
          {activeEnergyTypes.includes("ELEC") && (
            <div className="energy-kpi" style={{ background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" }}>
              <div className="energy-kpi-content">
                <div>
                  <p className="energy-kpi-label">{"\u00c9"}lectricit{"\u00e9"}</p>
                  <p className="energy-kpi-value">{kpisLoading ? "..." : formatLargeNumber(elecKpi?.total || 0)}</p>
                  <p className="energy-kpi-unit">TEP</p>
                </div>
                <div className="energy-kpi-icon"><Zap size={26} /></div>
              </div>
            </div>
          )}
          {activeEnergyTypes.includes("GASOIL") && (
            <div className="energy-kpi" style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}>
              <div className="energy-kpi-content">
                <div>
                  <p className="energy-kpi-label">Gasoil</p>
                  <p className="energy-kpi-value">{kpisLoading ? "..." : formatLargeNumber(gasoilKpi?.total || 0)}</p>
                  <p className="energy-kpi-unit">TEP</p>
                </div>
                <div className="energy-kpi-icon"><Fuel size={26} /></div>
              </div>
            </div>
          )}
          {activeEnergyTypes.includes("FUEL") && (
            <div className="energy-kpi" style={{ background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" }}>
              <div className="energy-kpi-content">
                <div>
                  <p className="energy-kpi-label">Fuel</p>
                  <p className="energy-kpi-value">{kpisLoading ? "..." : formatLargeNumber(fuelKpi?.total || 0)}</p>
                  <p className="energy-kpi-unit">TEP</p>
                </div>
                <div className="energy-kpi-icon"><Flame size={26} /></div>
              </div>
            </div>
          )}
          {activeEnergyTypes.includes("ESSENCE") && (
            <div className="energy-kpi" style={{ background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" }}>
              <div className="energy-kpi-content">
                <div>
                  <p className="energy-kpi-label">Essence</p>
                  <p className="energy-kpi-value">{kpisLoading ? "..." : formatLargeNumber(essenceKpi?.total || 0)}</p>
                  <p className="energy-kpi-unit">TEP</p>
                </div>
                <div className="energy-kpi-icon"><Droplets size={26} /></div>
              </div>
            </div>
          )}
          {activeEnergyTypes.includes("AUTRES") && (
            <div className="energy-kpi" style={{ background: "linear-gradient(135deg, #64748b 0%, #475569 100%)" }}>
              <div className="energy-kpi-content">
                <div>
                  <p className="energy-kpi-label">Autres</p>
                  <p className="energy-kpi-value">{kpisLoading ? "..." : formatLargeNumber(autresKpi?.total || 0)}</p>
                  <p className="energy-kpi-unit">TEP</p>
                </div>
                <div className="energy-kpi-icon"><Package size={26} /></div>
              </div>
            </div>
          )}
        </div>

        {/* Evolution Consommation par Type (TEP) - from KPI time_series */}
        {tepByTypeBarData && (
          <div className="energy-card energy-chart-card" style={{ marginBottom: 20, padding: "20px 24px" }}>
            <h3 className="energy-chart-title">
              <BarChart3 size={18} style={{ color: ACCENT }} />
              Evolution Consommation par Type (TEP)
            </h3>
            <div className="energy-chart-container energy-chart-container--tall">
              <Bar data={tepByTypeBarData} options={stackedBarOptions} />
            </div>
          </div>
        )}

        {/* Charts loading */}
        {anyLoading && (
          <div className="energy-card energy-state-card">
            <div className="energy-spinner" style={{ borderTopColor: ACCENT }} />
            <p className="energy-state-text">Chargement des graphiques...</p>
          </div>
        )}

        {/* No data */}
        {!anyLoading && !kpisLoading && !hasAnyChartData && (
          <div className="energy-card energy-state-card">
            <Zap size={56} style={{ color: "#cbd5e1" }} />
            <h3 className="energy-state-title">Aucune donn{"\u00e9"}e disponible</h3>
            <p className="energy-state-text">
              Aucune donn{"\u00e9"}e trouv{"\u00e9"}e pour les filtres s{"\u00e9"}lectionn{"\u00e9"}s.
            </p>
          </div>
        )}

        {/* ============================================ */}
        {/* PANEL: Par Activit\u00e9 (Activit\u00e9 + BU)          */}
        {/* ============================================ */}
        {!anyLoading && (activiteDataTep.length > 0 || activiteDataDh.length > 0 || buDataTep.length > 0 || buDataDh.length > 0) && (
          <div className="energy-panel">
            <button className="energy-panel-header" onClick={() => setPanelActivite((v) => !v)}>
              <div className="energy-panel-header-left">
                <Activity size={18} style={{ color: ACCENT }} />
                <span>Par Activit{"\u00e9"}</span>
              </div>
              <ChevronDown size={20} className={`energy-panel-chevron ${panelActivite ? "energy-panel-chevron--open" : ""}`} />
            </button>
            {panelActivite && (
              <div className="energy-panel-content">
                {/* Row 1: Activit\u00e9 pies (DH + TEP) */}
                <div className="energy-charts-grid">
                  {activitePieDh && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <PieChart size={18} style={{ color: ACCENT }} />
                        Charges (DH) par Activit{"\u00e9"}
                      </h3>
                      <div className="energy-chart-container energy-chart-container--pie">
                        <Doughnut data={activitePieDh} options={pieOptions} />
                      </div>
                    </div>
                  )}
                  {activitePieTep && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <PieChart size={18} style={{ color: ACCENT }} />
                        Consommation (TEP) par Activit{"\u00e9"}
                      </h3>
                      <div className="energy-chart-container energy-chart-container--pie">
                        <Doughnut data={activitePieTep} options={pieOptions} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Row 2: BU pies (DH + TEP) */}
                <div className="energy-charts-grid">
                  {buPieDh && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <PieChart size={18} style={{ color: ACCENT }} />
                        Charges (DH) par BU
                      </h3>
                      <div className="energy-chart-container energy-chart-container--pie">
                        <Doughnut data={buPieDh} options={pieOptions} />
                      </div>
                    </div>
                  )}
                  {buPieTep && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <PieChart size={18} style={{ color: ACCENT }} />
                        Consommation (TEP) par BU
                      </h3>
                      <div className="energy-chart-container energy-chart-container--pie">
                        <Doughnut data={buPieTep} options={pieOptions} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Row 3: BU evolution bars (DH + TEP) */}
                <div className="energy-charts-grid">
                  {buBarDh && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <BarChart3 size={18} style={{ color: ACCENT }} />
                        Evolution Charges (DH) par BU
                      </h3>
                      <div className="energy-chart-container energy-chart-container--tall">
                        <Bar data={buBarDh} options={stackedBarOptions} />
                      </div>
                    </div>
                  )}
                  {buBarTep && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <BarChart3 size={18} style={{ color: ACCENT }} />
                        Evolution Consommation (TEP) par BU
                      </h3>
                      <div className="energy-chart-container energy-chart-container--tall">
                        <Bar data={buBarTep} options={stackedBarOptions} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* PANEL: Par R\u00e9gion (Site)                     */}
        {/* ============================================ */}
        {!anyLoading && (regionDataTep.length > 0 || regionDataDh.length > 0) && (
          <div className="energy-panel">
            <button className="energy-panel-header" onClick={() => setPanelRegion((v) => !v)}>
              <div className="energy-panel-header-left">
                <MapPin size={18} style={{ color: ACCENT }} />
                <span>Par R{"\u00e9"}gion</span>
              </div>
              <ChevronDown size={20} className={`energy-panel-chevron ${panelRegion ? "energy-panel-chevron--open" : ""}`} />
            </button>
            {panelRegion && (
              <div className="energy-panel-content">
                {/* Row 1: Region stacked bars (DH + TEP) */}
                <div className="energy-charts-grid">
                  {regionBarDh && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <BarChart3 size={18} style={{ color: ACCENT }} />
                        Charges (DH) par Région
                      </h3>
                      <div className="energy-chart-container">
                        <Bar data={regionBarDh} options={stackedBarOptions} />
                      </div>
                    </div>
                  )}
                  {regionBarTep && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <BarChart3 size={18} style={{ color: ACCENT }} />
                        Consommation (TEP) par Région
                      </h3>
                      <div className="energy-chart-container">
                        <Bar data={regionBarTep} options={stackedBarOptions} />
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", margin: "8px 0" }}>
                  <label style={{ marginRight: 8, color: "#475569", alignSelf: "center" }}>Afficher :</label>
                  <select
                    value={siteRankingTopN}
                    onChange={(e) => setSiteRankingTopN(Number(e.target.value))}
                    aria-label="Top N sites"
                    style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #e2e8f0", background: "white" }}
                  >
                    <option value={5}>Top 5</option>
                    <option value={10}>Top 10</option>
                    <option value={15}>Top 15</option>
                    <option value={0}>Tous</option>
                  </select>
                </div>

                {/* Row 2: Site rankings (DH + TEP) */}
                <div className="energy-charts-grid">
                  {siteRankDh && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <BarChart3 size={18} style={{ color: "#37daaf" }} />
                        Classement par Site (DH)
                      </h3>
                      <div className="energy-chart-container energy-chart-container--tall">
                        <Bar data={siteRankDh} options={horizontalBarOptions} />
                      </div>
                    </div>
                  )}
                  {siteRankTep && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <BarChart3 size={18} style={{ color: "#37daaf" }} />
                        Classement par Site (TEP)
                      </h3>
                      <div className="energy-chart-container energy-chart-container--tall">
                        <Bar data={siteRankTep} options={horizontalBarOptions} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* PANEL: Par Fili\u00e8re                           */}
        {/* ============================================ */}
        {!anyLoading && (filiereDataTep.length > 0 || filiereDataDh.length > 0) && (
          <div className="energy-panel">
            <button className="energy-panel-header" onClick={() => setPanelFiliere((v) => !v)}>
              <div className="energy-panel-header-left">
                <Layers size={18} style={{ color: ACCENT }} />
                <span>Par Fili{"\u00e8"}re</span>
              </div>
              <ChevronDown size={20} className={`energy-panel-chevron ${panelFiliere ? "energy-panel-chevron--open" : ""}`} />
            </button>
            {panelFiliere && (
              <div className="energy-panel-content">
                {/* Row 1: Fili\u00e8re pies (DH + TEP) */}
                <div className="energy-charts-grid">
                  {filierePieDh && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <PieChart size={18} style={{ color: ACCENT }} />
                        Charges (DH) par Fili{"\u00e8"}re
                      </h3>
                      <div className="energy-chart-container energy-chart-container--pie">
                        <Doughnut data={filierePieDh} options={pieOptions} />
                      </div>
                    </div>
                  )}
                  {filierePieTep && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <PieChart size={18} style={{ color: ACCENT }} />
                        Consommation (TEP) par Fili{"\u00e8"}re
                      </h3>
                      <div className="energy-chart-container energy-chart-container--pie">
                        <Doughnut data={filierePieTep} options={pieOptions} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Row 2: Fili\u00e8re evolution bars (DH + TEP) */}
                <div className="energy-charts-grid">
                  {filiereBarDh && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <BarChart3 size={18} style={{ color: ACCENT }} />
                        Evolution Charges (DH) par Fili{"\u00e8"}re
                      </h3>
                      <div className="energy-chart-container">
                        <Bar data={filiereBarDh} options={stackedBarOptions} />
                      </div>
                    </div>
                  )}
                  {filiereBarTep && (
                    <div className="energy-card energy-chart-card">
                      <h3 className="energy-chart-title">
                        <BarChart3 size={18} style={{ color: ACCENT }} />
                        Evolution Consommation (TEP) par Fili{"\u00e8"}re
                      </h3>
                      <div className="energy-chart-container">
                        <Bar data={filiereBarTep} options={stackedBarOptions} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {/* ============================================ */}
        {/* SECTION: Analyse détaillée par type d'énergie */}
        {/* ============================================ */}
        {(() => {
          // Build options with correct unit label (avoids extra useMemo per type)
          const buildTypeBarOptions = (unit: string) => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: true,
                position: "top" as const,
                labels: { usePointStyle: true, pointStyle: "circle" as const, font: { size: 10 }, color: "#475569", padding: 12, boxWidth: 8 },
                onClick: legendIsolateClickDataset,
              },
              tooltip: {
                backgroundColor: "#1e293b",
                titleFont: { size: 13, weight: "bold" as const },
                bodyFont: { size: 12 },
                padding: 12,
                cornerRadius: 8,
                callbacks: {
                  label: (ctx: any) => `${ctx.dataset.label}: ${formatLargeNumber(ctx.parsed.y ?? 0)} ${unit}`,
                  footer: (items: any[]) => {
                    const total = items.reduce((s: number, i: any) => s + (i.parsed.y ?? 0), 0);
                    return `Total: ${formatLargeNumber(total)} ${unit}`;
                  },
                },
              },
              datalabels: {
                display: (ctx: any) => ctx.datasetIndex === ctx.chart.data.datasets.length - 1,
                anchor: "end" as const,
                align: "top" as const,
                formatter: (_value: number, ctx: any) => {
                  const datasets = ctx.chart.data.datasets;
                  let total = 0;
                  for (const ds of datasets) total += ds.data[ctx.dataIndex] ?? 0;
                  return `${formatLargeNumber(total)} ${unit}`;
                },
                color: "#334155",
                font: { size: 10, weight: "bold" as const },
              },
            },
            scales: {
              y: {
                stacked: true,
                beginAtZero: true,
                grid: { color: "#f1f5f9" },
                ticks: { color: "#94a3b8", font: { size: 11 }, callback: (v: string | number) => formatLargeNumber(Number(v)) },
                title: { display: true, text: unit, color: "#94a3b8", font: { size: 11 } },
              },
              x: { stacked: true, grid: { display: false }, ticks: { color: "#94a3b8", font: { size: 11 } } },
            },
          });

          // 5 display panels: 4 known + AUTRES
          const DISPLAY_TYPES = [
            { code: "ELEC", label: "Électricité", unit: "KWh", color: "#3b82f6" },
            { code: "GASOIL", label: "Gasoil", unit: "L", color: "#16a34a" },
            { code: "ESSENCE", label: "Essence", unit: "L", color: "#8b5cf6" },
            { code: "FUEL", label: "Fuel", unit: "kg", color: "#ef4444" },
            { code: "AUTRES", label: "Autres", unit: "kg", color: "#f59e0b" },
          ] as const;

          const hasAnyTypeData =
            Object.values(rawEnergieByType).some((t) => t.groups.length > 0) ||
            autresGroupsData.groups.length > 0;

          if (!hasAnyTypeData && !rawTypesLoading) return null;

          return (
            <div className="energy-panel">
              <button
                className="energy-panel-header"
                onClick={() => setPanelDetailType((v) => !v)}
              >
                <div className="energy-panel-header-left">
                  <BarChart3 size={18} style={{ color: ACCENT }} />
                  <span>Analyse d{"\u00e9"}taill{"\u00e9"}e par type d&apos;{"\u00e9"}nergie</span>
                </div>
                <ChevronDown
                  size={20}
                  className={`energy-panel-chevron ${panelDetailType ? "energy-panel-chevron--open" : ""}`}
                />
              </button>

              {panelDetailType && (
                <div className="energy-panel-content">

                  {/* Type selector toggle buttons */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16, padding: "12px 16px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b", alignSelf: "center", marginRight: 4 }}>
                      Afficher :
                    </span>
                    {DISPLAY_TYPES.map(({ code, label, unit, color }) => {
                      const isActive = selectedDetailTypes.includes(code);
                      return (
                        <button
                          key={code}
                          onClick={() => {
                            setSelectedDetailTypes((prev) => {
                              if (prev.includes(code)) {
                                // Prevent deselecting the last one
                                if (prev.length === 1) return prev;
                                return prev.filter((c) => c !== code);
                              }
                              return [...prev, code];
                            });
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "5px 12px",
                            borderRadius: 20,
                            border: `1.5px solid ${isActive ? color : "#e2e8f0"}`,
                            background: isActive ? `${color}18` : "#fff",
                            color: isActive ? color : "#94a3b8",
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: "pointer",
                            transition: "all 0.15s",
                            opacity: isActive ? 1 : 0.55,
                          }}
                        >
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: isActive ? color : "#cbd5e1", flexShrink: 0 }} />
                          {label}
                          <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.75 }}>({unit})</span>
                        </button>
                      );
                    })}
                    {selectedDetailTypes.length !== DISPLAY_TYPES.length && (
                      <button
                        onClick={() => setSelectedDetailTypes(DISPLAY_TYPES.map(t => t.code))}
                        style={{ padding: "5px 10px", borderRadius: 20, border: "1.5px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 11, fontWeight: 500, cursor: "pointer" }}
                      >
                        Tout afficher
                      </button>
                    )}
                  </div>

                  {rawTypesLoading && (
                    <div className="energy-card energy-state-card" style={{ marginBottom: 12 }}>
                      <div className="energy-spinner" style={{ borderTopColor: ACCENT }} />
                      <p className="energy-state-text">Chargement des donn{"\u00e9"}es par type...</p>
                    </div>
                  )}

                  {DISPLAY_TYPES.filter(({ code }) =>
                    selectedDetailTypes.includes(code)
                  ).map(({ code, label, unit, color }) => {
                    const { groups, allMonths } =
                      code === "AUTRES"
                        ? autresGroupsData
                        : findTypeGroups(code);

                    if (groups.length === 0) return null;

                    const isOpen = openTypePanels[code] !== false;
                    const barOptions = buildTypeBarOptions(unit);
                    const haBarOptions = buildTypeBarOptions(`${unit}/ha`);

                    const pieData = buildPieData(groups, BU_COLORS);
                    const barData = buildStackedBarData(groups, allMonths, BU_COLORS, filterType === "campaigns");
                    const haBarData = buildPerHaBarData(groups, buDataSupHa, allMonths, BU_COLORS, filterType === "campaigns");

                    return (
                      <div
                        key={code}
                        className="energy-panel"
                        style={{ marginBottom: 12, border: `1px solid ${color}22`, borderRadius: 10 }}
                      >
                        {/* Sub-panel header */}
                        <button
                          className="energy-panel-header"
                          style={{ borderRadius: isOpen ? "10px 10px 0 0" : 10 }}
                          onClick={() =>
                            setOpenTypePanels((prev) => ({ ...prev, [code]: !isOpen }))
                          }
                        >
                          <div className="energy-panel-header-left">
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                background: `${color}22`,
                                color,
                                fontWeight: 700,
                                fontSize: 13,
                              }}
                            >
                              {code === "ELEC" ? <Zap size={16} /> : code === "GASOIL" ? <Fuel size={16} /> : code === "ESSENCE" ? <Droplets size={16} /> : code === "FUEL" ? <Flame size={16} /> : <Package size={16} />}
                            </span>
                            <span style={{ fontWeight: 600, color: "#1e293b" }}>{label}</span>
                            <span
                              style={{
                                background: `${color}22`,
                                color,
                                borderRadius: 6,
                                padding: "2px 8px",
                                fontSize: 12,
                                fontWeight: 700,
                                marginLeft: 6,
                              }}
                            >
                              {unit}
                            </span>
                          </div>
                          <ChevronDown
                            size={18}
                            className={`energy-panel-chevron ${isOpen ? "energy-panel-chevron--open" : ""}`}
                          />
                        </button>

                        {isOpen && (
                          <div className="energy-panel-content">
                            {/* Row 1: Pie + Evolution bar side by side */}
                            <div className="energy-charts-grid">
                              {pieData && (
                                <div className="energy-card energy-chart-card">
                                  <h3 className="energy-chart-title">
                                    <PieChart size={16} style={{ color }} />
                                    Distribution par BU ({unit})
                                  </h3>
                                  <div className="energy-chart-container energy-chart-container--pie">
                                    <Doughnut data={pieData} options={pieOptions} />
                                  </div>
                                </div>
                              )}
                              {barData && (
                                <div className="energy-card energy-chart-card">
                                  <h3 className="energy-chart-title">
                                    <BarChart3 size={16} style={{ color }} />
                                    Evolution ({unit}) par BU —{" "}
                                    {filterType === "campaigns" ? "par campagne" : "par mois"}
                                  </h3>
                                  <div className="energy-chart-container energy-chart-container--pie">
                                    <Bar data={barData} options={barOptions} />
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Row 2: evolution par ha — full width */}
                            {haBarData && (
                              <div className="energy-card energy-chart-card" style={{ marginTop: 12 }}>
                                <h3 className="energy-chart-title">
                                  <BarChart3 size={16} style={{ color }} />
                                  Evolution ({unit}/ha) par BU —{" "}
                                  {filterType === "campaigns" ? "par campagne" : "par mois"}
                                </h3>
                                <div className="energy-chart-container energy-chart-container--tall">
                                  <Bar data={haBarData} options={haBarOptions} />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ============================================ */}
        {/* EnR — Evolution Consommation & Production PV */}
        {/* ============================================ */}
        {consoGWhBarData && (
          <div className="energy-card energy-chart-card" style={{ marginTop: 20, padding: "20px 24px" }}>
            <h3 className="energy-chart-title">
              <Zap size={18} style={{ color: ACCENT }} />
              Evolution Consommation &amp; Production PV (En service)
            </h3>
            <div className="energy-chart-container energy-chart-container--tall">
              <Chart
                type="bar"
                data={consoGWhBarData as any}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: true,
                      position: "bottom" as const,
                      labels: {
                        usePointStyle: true,
                        pointStyle: "circle" as const,
                        font: { size: 11 },
                        color: "#475569",
                        padding: 16,
                      },
                      onClick: legendIsolateClickDataset,
                    },
                    tooltip: {
                      backgroundColor: "#1e293b",
                      titleFont: { size: 13, weight: "bold" as const },
                      bodyFont: { size: 12 },
                      padding: 12,
                      cornerRadius: 8,
                      callbacks: {
                        label: (ctx: any) => {
                          if (ctx.dataset.yAxisID === "y2") {
                            return `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%`;
                          }
                          return `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)} GWh`;
                        },
                      },
                    },
                    datalabels: {
                      display: true,
                      anchor: (ctx: any) =>
                        ctx.dataset.type === "line" ? "center" : "end",
                      align: (ctx: any) =>
                        ctx.dataset.type === "line" ? "top" : "end",
                      formatter: (value: number, ctx: any) =>
                        ctx.dataset.type === "line"
                          ? value.toFixed(1)
                          : value > 0 ? value.toFixed(2) : "",
                      color: "#334155",
                      font: { size: 10, weight: "bold" as const },
                    },
                  },
                  scales: {
                    y: {
                      type: "linear" as const,
                      position: "left" as const,
                      beginAtZero: true,
                      grid: { color: "#f1f5f9" },
                      title: {
                        display: true,
                        text: "Valeur (GWh)",
                        color: "#64748b",
                        font: { size: 11 },
                      },
                      ticks: {
                        color: "#94a3b8",
                        font: { size: 11 },
                        callback: (value: string | number) =>
                          `${Number(value).toFixed(0)} GWh`,
                      },
                    },
                    y2: {
                      type: "linear" as const,
                      position: "right" as const,
                      beginAtZero: true,
                      grid: { drawOnChartArea: false },
                      title: {
                        display: true,
                        text: "Taux Autoconso (%)",
                        color: "#64748b",
                        font: { size: 11 },
                      },
                      ticks: {
                        color: "#94a3b8",
                        font: { size: 11 },
                        callback: (value: string | number) =>
                          `${Number(value).toFixed(1)}%`,
                      },
                    },
                    x: {
                      grid: { display: false },
                      ticks: {
                        color: "#94a3b8",
                        font: { size: 11 },
                        maxRotation: 45,
                        minRotation: 0,
                      },
                    },
                  },
                } as any}
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default EnergyPage;
